import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Search, Plus, Leaf, Edit, Trash2, ExternalLink, Eye, X, Save
} from "lucide-react";
import { toast } from "sonner";

import { API } from "@/config";

// FDA 9 Major Allergens (includes sesame as of 2023)
const MAJOR_ALLERGENS = [
  { id: "milk", label: "Milk" },
  { id: "eggs", label: "Eggs" },
  { id: "fish", label: "Fish" },
  { id: "shellfish", label: "Shellfish" },
  { id: "tree_nuts", label: "Tree Nuts" },
  { id: "peanuts", label: "Peanuts" },
  { id: "wheat", label: "Wheat" },
  { id: "soybeans", label: "Soybeans" },
  { id: "sesame", label: "Sesame" }
];

const EMPTY_NUTRITION = {
  name: "",
  serving_size: 100,
  serving_unit: "g",
  calories: "",
  protein: "",
  total_fat: "",
  saturated_fat: "",
  trans_fat: "0",
  cholesterol: "",
  sodium: "",
  total_carbs: "",
  dietary_fiber: "",
  sugars: "",
  added_sugars: "0",
  vitamin_d: "0",
  calcium: "",
  iron: "",
  potassium: "",
  ingredient_statement: "",
  allergens: {
    milk: false, eggs: false, fish: false, shellfish: false,
    tree_nuts: false, peanuts: false, wheat: false, soybeans: false, sesame: false
  }
};

const RDNutritionPage = () => {
  const [activeTab, setActiveTab] = useState("local");
  const [localNutrition, setLocalNutrition] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [usdaSearch, setUsdaSearch] = useState("");
  const [usdaResults, setUsdaResults] = useState([]);
  const [usdaLoading, setUsdaLoading] = useState(false);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUsdaDetailModal, setShowUsdaDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [usdaDetailLoading, setUsdaDetailLoading] = useState(false);
  const [usdaDetailData, setUsdaDetailData] = useState(null);
  
  const [formData, setFormData] = useState({ ...EMPTY_NUTRITION });

  useEffect(() => { fetchLocalNutrition(); }, []);

  const fetchLocalNutrition = async () => {
    try {
      const response = await axios.get(`${API}/rd/nutrition`);
      setLocalNutrition(response.data);
    } catch (error) {
      console.error('Failed to fetch nutrition data', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchUSDA = async () => {
    if (!usdaSearch.trim()) return;
    setUsdaLoading(true);
    try {
      const response = await axios.get(`${API}/rd/nutrition/usda/search?query=${encodeURIComponent(usdaSearch)}`);
      setUsdaResults(response.data.foods || []);
    } catch (error) {
      console.error('Failed to search USDA', error);
      toast.error("Failed to search USDA database");
    } finally {
      setUsdaLoading(false);
    }
  };

  const handleViewUsdaDetail = async (usdaItem) => {
    setUsdaDetailLoading(true);
    setShowUsdaDetailModal(true);
    setUsdaDetailData(null);
    
    try {
      const response = await axios.get(`${API}/rd/nutrition/usda/detail/${usdaItem.fdcId}`);
      setUsdaDetailData(response.data);
    } catch (error) {
      // Create a basic detail view from search result
      setUsdaDetailData({
        fdcId: usdaItem.fdcId,
        description: usdaItem.description,
        dataType: usdaItem.dataType,
        brandName: usdaItem.brandName,
        ingredients: usdaItem.ingredients || "",
        foodNutrients: []
      });
    } finally {
      setUsdaDetailLoading(false);
    }
  };

  const handleImportFromUSDA = async (fdcId, description) => {
    try {
      const response = await axios.post(`${API}/rd/nutrition/usda/import`, { fdc_id: fdcId });
      setLocalNutrition(prev => [...prev, response.data]);
      toast.success(`Imported: ${description}`);
      setShowUsdaDetailModal(false);
    } catch (error) {
      console.error('Failed to import from USDA', error);
      toast.error("Failed to import from USDA");
    }
  };

  const handleOpenAdd = () => {
    setFormData({ ...EMPTY_NUTRITION });
    setShowAddModal(true);
  };

  const handleOpenEdit = (item) => {
    // Convert allergens array to checkbox object if needed
    let allergens = { milk: false, eggs: false, fish: false, shellfish: false, tree_nuts: false, peanuts: false, wheat: false, soybeans: false, sesame: false };
    if (Array.isArray(item.allergens)) {
      item.allergens.forEach(a => {
        const key = a.toLowerCase().replace(/\s+/g, '_');
        if (allergens.hasOwnProperty(key)) allergens[key] = true;
      });
    } else if (item.allergens && typeof item.allergens === 'object') {
      allergens = { ...allergens, ...item.allergens };
    }
    
    setSelectedItem(item);
    setFormData({
      ...item,
      allergens
    });
    setShowEditModal(true);
  };

  const handleSaveNutrition = async (isEdit = false) => {
    // Convert allergens object to array for storage
    const allergensArray = MAJOR_ALLERGENS
      .filter(a => formData.allergens[a.id])
      .map(a => a.label);
    
    const payload = {
      ...formData,
      source: isEdit ? formData.source : "manual",
      calories: parseFloat(formData.calories) || 0,
      protein: parseFloat(formData.protein) || 0,
      total_fat: parseFloat(formData.total_fat) || 0,
      saturated_fat: parseFloat(formData.saturated_fat) || 0,
      trans_fat: parseFloat(formData.trans_fat) || 0,
      cholesterol: parseFloat(formData.cholesterol) || 0,
      sodium: parseFloat(formData.sodium) || 0,
      total_carbs: parseFloat(formData.total_carbs) || 0,
      dietary_fiber: parseFloat(formData.dietary_fiber) || 0,
      sugars: parseFloat(formData.sugars) || 0,
      added_sugars: parseFloat(formData.added_sugars) || 0,
      vitamin_d: parseFloat(formData.vitamin_d) || 0,
      calcium: parseFloat(formData.calcium) || 0,
      iron: parseFloat(formData.iron) || 0,
      potassium: parseFloat(formData.potassium) || 0,
      allergens: allergensArray
    };

    try {
      if (isEdit && selectedItem) {
        await axios.put(`${API}/rd/nutrition/${selectedItem.id}`, payload);
        setLocalNutrition(prev => prev.map(n => n.id === selectedItem.id ? { ...n, ...payload } : n));
        toast.success("Nutrition entry updated");
        setShowEditModal(false);
      } else {
        const response = await axios.post(`${API}/rd/nutrition`, payload);
        setLocalNutrition(prev => [...prev, response.data]);
        toast.success("Nutrition entry created");
        setShowAddModal(false);
      }
    } catch (error) {
      // Fallback for demo
      if (isEdit && selectedItem) {
        setLocalNutrition(prev => prev.map(n => n.id === selectedItem.id ? { ...n, ...payload } : n));
        toast.success("Nutrition entry updated");
        setShowEditModal(false);
      } else {
        const created = { ...payload, id: `nutr-${Date.now()}` };
        setLocalNutrition(prev => [...prev, created]);
        toast.success("Nutrition entry created");
        setShowAddModal(false);
      }
    }
    setFormData({ ...EMPTY_NUTRITION });
  };

  const handleDelete = async (item) => {
    if (!confirm(`Delete nutrition entry for "${item.name}"?`)) return;
    try {
      await axios.delete(`${API}/rd/nutrition/${item.id}`);
      setLocalNutrition(prev => prev.filter(n => n.id !== item.id));
      toast.success("Entry deleted");
    } catch (error) {
      setLocalNutrition(prev => prev.filter(n => n.id !== item.id));
      toast.success("Entry deleted");
    }
  };

  const toggleAllergen = (allergenId) => {
    setFormData(prev => ({
      ...prev,
      allergens: { ...prev.allergens, [allergenId]: !prev.allergens[allergenId] }
    }));
  };

  const getActiveAllergens = (item) => {
    if (Array.isArray(item.allergens)) return item.allergens;
    if (item.allergens && typeof item.allergens === 'object') {
      return MAJOR_ALLERGENS.filter(a => item.allergens[a.id]).map(a => a.label);
    }
    return [];
  };

  const filteredLocal = localNutrition.filter(item =>
    searchQuery === "" || item.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Leaf className="w-8 h-8 text-green-600" />
            Nutrition Database
          </h1>
          <p className="text-gray-600">Manage nutritional information, ingredients, and allergens</p>
        </div>
        <Button onClick={handleOpenAdd}>
          <Plus className="w-4 h-4 mr-2" />Add Manual Entry
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Total Entries</div>
            <div className="text-2xl font-bold">{localNutrition.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">From USDA</div>
            <div className="text-2xl font-bold text-blue-600">
              {localNutrition.filter(n => n.source === 'usda').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Manual Entries</div>
            <div className="text-2xl font-bold text-purple-600">
              {localNutrition.filter(n => n.source === 'manual').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">With Allergens</div>
            <div className="text-2xl font-bold text-orange-600">
              {localNutrition.filter(n => getActiveAllergens(n).length > 0).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="bg-white rounded-lg border">
        <TabsList className="w-full justify-start border-b rounded-none px-2 bg-gray-50">
          <TabsTrigger value="local">Local Database ({localNutrition.length})</TabsTrigger>
          <TabsTrigger value="usda">Search USDA</TabsTrigger>
        </TabsList>

        <TabsContent value="local" className="p-4">
          <div className="mb-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search local entries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Source</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Cal</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Protein</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Fat</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Carbs</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Allergens</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredLocal.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                      No nutrition entries found. Add one manually or import from USDA.
                    </td>
                  </tr>
                ) : (
                  filteredLocal.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium">{item.name}</div>
                        <div className="text-xs text-gray-500">{item.serving_size}{item.serving_unit} serving</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={item.source === 'usda' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                          {item.source}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">{item.calories}</td>
                      <td className="px-4 py-3 text-right">{item.protein}g</td>
                      <td className="px-4 py-3 text-right">{item.total_fat}g</td>
                      <td className="px-4 py-3 text-right">{item.total_carbs}g</td>
                      <td className="px-4 py-3">
                        {getActiveAllergens(item).length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {item.allergens_review_needed && (
                              <Badge className="bg-yellow-100 text-yellow-700 text-[10px]">⚠️ Review</Badge>
                            )}
                            {getActiveAllergens(item).map(a => (
                              <Badge key={a} className="bg-orange-100 text-orange-700 text-[10px]">{a}</Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">None</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleOpenEdit(item)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(item)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="usda" className="p-4">
          <div className="mb-4">
            <div className="flex gap-2 max-w-lg">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search USDA FoodData Central..."
                  value={usdaSearch}
                  onChange={(e) => setUsdaSearch(e.target.value)}
                  className="pl-10"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchUSDA()}
                />
              </div>
              <Button onClick={handleSearchUSDA} disabled={usdaLoading}>
                {usdaLoading ? "Searching..." : "Search"}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">Search the USDA FoodData Central database. Click an item to view full details.</p>
          </div>

          {usdaResults.length > 0 && (
            <div className="border rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Description</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Brand</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">FDC ID</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {usdaResults.map((item, idx) => (
                    <tr 
                      key={idx} 
                      className="hover:bg-blue-50 cursor-pointer"
                      onClick={() => handleViewUsdaDetail(item)}
                    >
                      <td className="px-4 py-3 font-medium">{item.description}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline">{item.dataType}</Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{item.brandName || '-'}</td>
                      <td className="px-4 py-3 text-gray-500">{item.fdcId}</td>
                      <td className="px-4 py-3">
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleViewUsdaDetail(item); }}>
                          <Eye className="w-4 h-4 mr-1" />View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal || showEditModal} onOpenChange={(open) => { if (!open) { setShowAddModal(false); setShowEditModal(false); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showEditModal ? 'Edit Nutrition Entry' : 'Add Nutrition Entry'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Item Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div>
                <Label>Serving Size</Label>
                <Input type="number" value={formData.serving_size} onChange={(e) => setFormData({ ...formData, serving_size: e.target.value })} />
              </div>
              <div>
                <Label>Serving Unit</Label>
                <Input value={formData.serving_unit} onChange={(e) => setFormData({ ...formData, serving_unit: e.target.value })} />
              </div>
            </div>

            <div className="border-t pt-4">
              <Label className="text-sm font-medium">Major Allergens (FDA 9)</Label>
              <p className="text-xs text-gray-500 mb-2">Check all allergens present in this ingredient</p>
              <div className="grid grid-cols-3 gap-2">
                {MAJOR_ALLERGENS.map(allergen => (
                  <label 
                    key={allergen.id}
                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                      formData.allergens[allergen.id] ? 'bg-orange-50 border-orange-300' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.allergens[allergen.id] || false}
                      onChange={() => toggleAllergen(allergen.id)}
                      className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm">{allergen.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <Label>Ingredient Statement</Label>
              <p className="text-xs text-gray-500 mb-1">Full ingredient list for this item</p>
              <Textarea 
                value={formData.ingredient_statement} 
                onChange={(e) => setFormData({ ...formData, ingredient_statement: e.target.value })} 
                className="h-20"
                placeholder="e.g., Whole Eggs, Skim Milk, Soybean Oil, Salt..."
              />
            </div>

            <div className="border-t pt-4">
              <Label className="text-sm font-medium">Nutrition Facts (per serving)</Label>
              <div className="grid grid-cols-4 gap-3 mt-2">
                <div>
                  <Label className="text-xs">Calories</Label>
                  <Input type="number" value={formData.calories} onChange={(e) => setFormData({ ...formData, calories: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Protein (g)</Label>
                  <Input type="number" value={formData.protein} onChange={(e) => setFormData({ ...formData, protein: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Total Fat (g)</Label>
                  <Input type="number" value={formData.total_fat} onChange={(e) => setFormData({ ...formData, total_fat: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Saturated Fat (g)</Label>
                  <Input type="number" value={formData.saturated_fat} onChange={(e) => setFormData({ ...formData, saturated_fat: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Trans Fat (g)</Label>
                  <Input type="number" value={formData.trans_fat} onChange={(e) => setFormData({ ...formData, trans_fat: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Cholesterol (mg)</Label>
                  <Input type="number" value={formData.cholesterol} onChange={(e) => setFormData({ ...formData, cholesterol: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Sodium (mg)</Label>
                  <Input type="number" value={formData.sodium} onChange={(e) => setFormData({ ...formData, sodium: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Total Carbs (g)</Label>
                  <Input type="number" value={formData.total_carbs} onChange={(e) => setFormData({ ...formData, total_carbs: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Dietary Fiber (g)</Label>
                  <Input type="number" value={formData.dietary_fiber} onChange={(e) => setFormData({ ...formData, dietary_fiber: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Sugars (g)</Label>
                  <Input type="number" value={formData.sugars} onChange={(e) => setFormData({ ...formData, sugars: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Added Sugars (g)</Label>
                  <Input type="number" value={formData.added_sugars} onChange={(e) => setFormData({ ...formData, added_sugars: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Vitamin D (mcg)</Label>
                  <Input type="number" value={formData.vitamin_d} onChange={(e) => setFormData({ ...formData, vitamin_d: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Calcium (mg)</Label>
                  <Input type="number" value={formData.calcium} onChange={(e) => setFormData({ ...formData, calcium: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Iron (mg)</Label>
                  <Input type="number" value={formData.iron} onChange={(e) => setFormData({ ...formData, iron: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Potassium (mg)</Label>
                  <Input type="number" value={formData.potassium} onChange={(e) => setFormData({ ...formData, potassium: e.target.value })} />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>Cancel</Button>
            <Button onClick={() => handleSaveNutrition(showEditModal)} disabled={!formData.name}>
              <Save className="w-4 h-4 mr-1" />{showEditModal ? 'Save Changes' : 'Create Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* USDA Detail Modal */}
      <Dialog open={showUsdaDetailModal} onOpenChange={setShowUsdaDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-blue-600" />
              USDA Food Details
            </DialogTitle>
          </DialogHeader>
          
          {usdaDetailLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : usdaDetailData ? (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold">{usdaDetailData.description}</h3>
                <div className="flex gap-2 mt-1">
                  <Badge variant="outline">{usdaDetailData.dataType}</Badge>
                  <Badge className="bg-blue-100 text-blue-700">FDC ID: {usdaDetailData.fdcId}</Badge>
                  {usdaDetailData.brandName && <Badge>{usdaDetailData.brandName}</Badge>}
                </div>
              </div>

              {usdaDetailData.ingredients && (
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium">Ingredient Statement</Label>
                  <div className="bg-gray-50 rounded p-3 mt-1 text-sm max-h-32 overflow-y-auto">
                    {usdaDetailData.ingredients}
                  </div>
                </div>
              )}

              {usdaDetailData.foodNutrients && usdaDetailData.foodNutrients.length > 0 && (
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium">Nutrition Facts (per 100g)</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2 max-h-64 overflow-y-auto">
                    {usdaDetailData.foodNutrients.slice(0, 20).map((n, idx) => (
                      <div key={idx} className="flex justify-between p-2 bg-gray-50 rounded text-sm">
                        <span className="text-gray-600">{n.nutrient?.name || n.nutrientName}</span>
                        <span className="font-medium">{n.amount} {n.nutrient?.unitName || n.unitName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No data available</div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUsdaDetailModal(false)}>Close</Button>
            {usdaDetailData && (
              <Button onClick={() => handleImportFromUSDA(usdaDetailData.fdcId, usdaDetailData.description)}>
                <Plus className="w-4 h-4 mr-1" />Import to Local Database
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RDNutritionPage;
